import { band as bandOf, fillRatio } from '@transport-booking/shared'

/*
 * Dates follow the chosen language.
 *
 * Every formatter here was pinned to 'en-GB', so an app with nineteen
 * languages printed "Thursday 15 October" on a right-to-left Arabic page and
 * did the same in Urdu, Persian and Chinese. The labels were translated and
 * the values were not, which is the version of half-translated that looks
 * most like carelessness.
 *
 * A module-level value rather than a hook, because these are called from
 * plain functions all over the app and threading a locale through every call
 * site would be a hundred edits to fix one bug. I18nProvider keeps it in step
 * with the language; `undefined` would take the BROWSER's locale, which is a
 * different thing from the one the person picked, so it is never used.
 */
let dateLocale = 'en-GB'

/**
 * Every departure is shown in SAUDI time, on every phone, in every country,
 * and says so.
 *
 * These times were rendered in the device's own zone, so a pilgrim reading
 * the schedule from Karachi saw a 14:00 Makkah departure as 16:00 and a
 * family in London saw 12:00. Nothing on the screen said which it was. The
 * vehicle leaves when it leaves: the departure is a fact about Makkah, not
 * about where the reader is standing, so it is pinned to one zone and
 * labelled - a converted time with no label is the thing that makes someone
 * miss a coach.
 */
const TZ = 'Asia/Riyadh'

/*
 * Every time this file returns is wrapped in a directional isolate.
 *
 * A clock is bidi-neutral in the middle and the meridiem is a Latin run, so
 * inside an Urdu or Arabic line "10:00 AM KSA" was reordered to "AM KSA 10:00"
 * -- and on a carrier's departures row it came out as "PM . Hiace Hi-Roof 1
 * 04:00", with the meridiem on the far side of the vehicle name. The isolate
 * keeps the time as one unbreakable thing and lets the line order it as a
 * whole.
 */
const ISO_L = '\u2068'
const ISO_R = '\u2069'

/** The short marker printed after a time. Translated; 'KSA' until it is. */
let tzLabel = 'KSA'

/** Called by I18nProvider whenever the language changes. */
export function setDateLocale(locale: string): void {
  dateLocale = locale || 'en-GB'
}

/** Likewise -- the marker is catalogue copy, and this file cannot call a hook. */
export function setTzLabel(label: string): void {
  tzLabel = label || 'KSA'
}

export type Band = 'green' | 'amber' | 'red'

export const bandFor = (used: number, capacity: number): Band =>
  bandOf(fillRatio(used, capacity))

export const BAND_CLASS: Record<Band, { bar: string; pill: string; rule: string }> = {
  green: { bar: 'bg-band-green', pill: 'bg-band-green-wash text-band-green border-band-green/30', rule: 'border-l-band-green' },
  amber: { bar: 'bg-band-amber', pill: 'bg-band-amber-wash text-band-amber border-band-amber/30', rule: 'border-l-band-amber' },
  red:   { bar: 'bg-band-red',   pill: 'bg-band-red-wash text-band-red border-band-red/30',       rule: 'border-l-band-red' },
}

/**
 * `locale` defaults to the reader's, not to English. Default parameters are
 * evaluated per call, so this picks up whatever I18nProvider last set - and
 * every one of the 33 call sites omits the argument.
 */
export function money(amount: number | string | null | undefined, currency: string, locale = dateLocale): string {
  if (amount === null || amount === undefined) return '—'
  const n = typeof amount === 'string' ? Number(amount) : amount
  if (Number.isNaN(n)) return '—'
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency', currency,
      maximumFractionDigits: currency === 'PKR' || currency === 'NOK' ? 0 : 2,
    }).format(n)
  } catch {
    return `${currency} ${n.toLocaleString(locale)}`
  }
}

/**
 * "4h 30m", "45m", or nothing when the route has no duration on it.
 *
 * Latin digits and the letters h and m, untranslated -- exactly what the
 * carrier's own site has printed since it was built, and the same string on
 * both products rather than two spellings of one number. Under the hour
 * stays in minutes: "80m" in a column beside "1h 40m" reads as a mistake.
 */
export function duration(min: number | null | undefined): string | null {
  if (!min || min <= 0) return null
  if (min < 60) return `${min}m`
  const h = Math.floor(min / 60)
  const rest = min % 60
  return rest ? `${h}h ${rest}m` : `${h}h`
}

export const kg = (n: number | string | null | undefined): string =>
  n === null || n === undefined ? '—' : `${Math.round(Number(n)).toLocaleString('en')} kg`

export const m3 = (n: number | string | null | undefined): string =>
  n === null || n === undefined ? '—' : `${Number(n).toFixed(2)} m³`

/**
 * The hour of a departure ON THE CLOCK THIS APP PRINTS -- Riyadh, not the
 * device.
 *
 * Caught by clicking, 2026-10-12: the new "night, after 18:00" filter used
 * `new Date(iso).getHours()`, which is the phone's own timezone, while every
 * time on the card beside it is formatted in Asia/Riyadh. On a laptop two
 * hours off KSA the filter kept an 08:00 departure as a night one -- the
 * list said "1 of 3" and showed a morning coach, which is worse than having
 * no filter at all.
 */
export function hourOf(iso: string): number {
  return Number(new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit', hour12: false, timeZone: TZ,
  }).format(new Date(iso)))
}

/** Just the clock, for a departure whose day is already on the line. */
export const time = (iso: string): string =>
  `${ISO_L}${new Date(iso).toLocaleTimeString(dateLocale, {
    hour: '2-digit', minute: '2-digit', timeZone: TZ,
  })}\u00a0${tzLabel}${ISO_R}`

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${ISO_L}${new Intl.DateTimeFormat(dateLocale, {
    weekday: 'short', day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ,
  }).format(d)}\u00a0${tzLabel}${ISO_R}`
}

/**
 * Parse 'YYYY-MM-DD' as LOCAL midnight, not UTC.
 *
 * `new Date('2026-08-30')` is UTC midnight by spec, which formats as the 29th
 * anywhere behind UTC. A trip_date is a calendar day, not an instant, so it
 * must be built from its own parts.
 */
function asLocalDate(iso: string): Date {
  const s = iso.trim()

  // A bare date, or a Postgres DATE serialised through a JS Date -- both are
  // calendar days and must be built from their own parts, or they render as
  // the day before west of Greenwich.
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?Z)?$/.exec(s)
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))

  // Anything else carries a real time of day: it is an instant, and the local
  // rendering of that instant is the right answer.
  return new Date(s)
}

export function dateOnly(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = asLocalDate(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${ISO_L}${new Intl.DateTimeFormat(dateLocale, {
    weekday: 'long', day: '2-digit', month: 'long',
  }).format(d)}${ISO_R}`
}

/**
 * Month and YEAR, for a licence expiry.
 *
 * dateOnly() gives "Tuesday, 14 March" -- a weekday and no year, which is
 * right for a departure three days away and actively misleading on a
 * document that expires in 2028. A traveller reading "valid to Tuesday, 14
 * March" cannot tell which March, and the whole value of the directory is
 * that the papers can be read.
 *
 * Isolated like every other date here, so a Latin year cannot be torn out of
 * an Arabic sentence by the bidi algorithm.
 */
/**
 * A full calendar date, with the year.
 *
 * For anything that is not "soon": a licence expiry, the day a bookmark was
 * made. dateOnly() deliberately drops the year because a departure this week
 * does not need one; everywhere else its absence is a bug waiting to be
 * reported as "which March?".
 */
export function dateFull(iso: string | null | undefined): string {
  if (!iso) return '\u2014'
  const d = asLocalDate(iso)
  if (Number.isNaN(d.getTime())) return '\u2014'
  return `${ISO_L}${new Intl.DateTimeFormat(dateLocale, {
    day: '2-digit', month: 'long', year: 'numeric',
  }).format(d)}${ISO_R}`
}

export function monthYear(iso: string | null | undefined): string {
  if (!iso) return '\u2014'
  const d = asLocalDate(iso)
  if (Number.isNaN(d.getTime())) return '\u2014'
  return `${ISO_L}${new Intl.DateTimeFormat(dateLocale, {
    month: 'short', year: 'numeric',
  }).format(d)}${ISO_R}`
}

export function relative(iso: string | null | undefined): string {
  if (!iso) return '—'
  const diffMs = new Date(iso).getTime() - Date.now()
  const abs = Math.abs(diffMs)
  const rtf = new Intl.RelativeTimeFormat(dateLocale, { numeric: 'auto' })
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['day', 86_400_000], ['hour', 3_600_000], ['minute', 60_000],
  ]
  for (const [unit, ms] of units) {
    if (abs >= ms || unit === 'minute') return rtf.format(Math.round(diffMs / ms), unit)
  }
  return '—'
}

/**
 * The client-facing order timeline. Deliberately plain words — the client is
 * not reading a state machine, she is asking "where are my things".
 */
export const STEPS = [
  // label and hint are catalogue keys -- `step.<key>` and `stepHint.<key>` --
  // resolved where they are rendered. This module has no hook to call.
  { key: 'requested' },
  { key: 'quoted' },
  { key: 'approved' },
  { key: 'received_at_origin' },
  { key: 'invoiced' },
  { key: 'arrived_at_destination' },
  { key: 'done' },
] as const

export function stepIndexFor(status: string): number {
  if (status === 'collected' || status === 'delivered') return 6
  const i = STEPS.findIndex((s) => s.key === status)
  return i === -1 ? 2 : i
}

/** Statuses that have a friendlier word than the raw enum, as catalogue keys. */
export const ORDER_STATUS_LABEL: Record<string, string> = {
  approved: 'orderStatus.approved',
  received_at_origin: 'orderStatus.received_at_origin',
  invoiced: 'orderStatus.invoiced',
  arrived_at_destination: 'orderStatus.arrived_at_destination',
  collected: 'orderStatus.collected',
  delivered: 'orderStatus.delivered',
  cancelled_by_client: 'orderStatus.cancelled_by_client',
  cancelled_by_transporter: 'orderStatus.cancelled_by_transporter',
}

export const ORDER_STATUS_TONE: Record<string, 'accent' | 'ok' | 'warn' | 'crit' | 'mute'> = {
  approved: 'accent',
  received_at_origin: 'accent',
  invoiced: 'accent',
  arrived_at_destination: 'warn',
  collected: 'ok',
  delivered: 'ok',
  cancelled_by_client: 'crit',
  cancelled_by_transporter: 'crit',
}
